import { IQueryProps } from '../interfaces/general/api';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ITask } from '@clickster/constant-definitions/dist-esm/entities/task/types';
import { TasksService } from '../services/tasks/tasks-rest';

interface IGetTasksProps extends IQueryProps<ITask[]> {
  cacheEnabled?: boolean;
  queryKey?: string;
}

export const useGetTasks = ({ params = {}, handleSuccess, queryKey = 'tasks' }: IGetTasksProps) => {
  const { data = [], isLoading, isFetching, refetch } = useQuery([queryKey], ({ signal }) => TasksService.list(params, signal), {
    onSuccess: (data) => {
      handleSuccess ? handleSuccess(data) : undefined;
    },
    refetchOnWindowFocus: false, 
  });

  const refetchInterval: number | false = Array.isArray(data) && data.some((task: any) => task?.status === 'STARTED') ? 2000 : false;

  return {
    tasks: data,
    isLoading,
    isFetching,
    refetch,
    refetchInterval,
  };
};

interface IGetTasksByCampaignProps extends IGetTasksProps {
  campaignId: string;
  withChangesOnly?: number;
  taskType?: 'ADS_AUTOMATION' | 'CAMPAIGN_BUDGET' | 'WL_AUTOMATION' | 'BLOCK_PLACEMENTS_AUTOMATION';
}

/**
 * Hook to fetch tasks filtered by campaign ID and task type.
 * Uses the API endpoint /tasks/:campaignId/executions?withChangesOnly=:withChangesOnly&taskType=:taskType
 * to retrieve tasks for the specified campaign.
 *
 * @param withChangesOnly - Filter tasks with changes: 0 for all logs (default), 1 for only tasks with changes/bid adjustments
 * @param taskType - Task type to filter by: 'ADS_AUTOMATION' (default), 'CAMPAIGN_BUDGET', 'WL_AUTOMATION', or 'BLOCK_PLACEMENTS_AUTOMATION'
 */
export const useGetTasksByCampaign = ({ campaignId, withChangesOnly = 0, taskType = 'ADS_AUTOMATION', params = {}, handleSuccess, queryKey = 'tasks' }: IGetTasksByCampaignProps) => {
  const { data = [], isLoading, isFetching, refetch } = useQuery(
    [queryKey, campaignId, withChangesOnly, taskType],
    async (context) => {
      const [, qCampaignId, qWithChangesOnly, qTaskType] = context.queryKey;
      return TasksService.listByCampaign(qCampaignId as string, qWithChangesOnly as number, qTaskType as any, params);
    },
    {
      onSuccess: (data) => {
        handleSuccess ? handleSuccess(data) : undefined;
      },
      retry: false,
      refetchOnWindowFocus: true, 
      cacheTime: 0, 
      staleTime: 0, 
      refetchInterval: false,
      enabled: !!campaignId,
    }
  );

  const refetchInterval: number | false = Array.isArray(data) && data.some((task: any) => task?.status === 'STARTED') ? 2000 : false;

  return {
    tasks: data,
    isLoading,
    isFetching,
    refetch,
    refetchInterval,
  };
};


export const useFindTask = (id: string, handleSuccess?: (data: ITask) => void) => {
  const { data, isLoading, isFetching, refetch } = useQuery(['task', id], () => TasksService.findTask(id), {
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!id,
  });

  return {
    task: data,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useDeleteTask = ({ handleSuccess }: IQueryProps<ITask>) => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation((id: string) => TasksService.deleteTask(id), {
    onSuccess: handleSuccess ? handleSuccess : undefined,
    retry: false,
  });

  return {
    deleteTask: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useCreateTask = ({ handleSuccess }: IQueryProps<ITask>) => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation((task: ITask) => TasksService.createTask(task), {
    onError: (error) => console.log({ error }),
    onSuccess: handleSuccess ? handleSuccess : undefined,
  });

  return {
    createTask: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useUpdateTask = ({ handleSuccess }: IQueryProps<ITask>) => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation((task: ITask) => TasksService.updateTask(task), {
    onError: (error) => console.log({ error }),
    onSuccess: handleSuccess ? handleSuccess : undefined,
  });

  return {
    updateTask: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useExecuteTask = ({ handleSuccess }: IQueryProps<ITask>) => {
  const { mutateAsync, isError, isLoading, isSuccess } = useMutation((taskId: string) => TasksService.executeTask(taskId), {
    onError: (error) => console.log({ error }),
    onSuccess: handleSuccess ? handleSuccess : undefined,
  });

  return {
    executeTask: mutateAsync,
    isError,
    isLoading,
    isSuccess,
  };
};

export const useMutateFindTask = (handleSuccess?: (data: ITask) => void) => {
  const { mutateAsync, isLoading, isError, data } = useMutation(
    (id: string) => TasksService.findTask(id),
    {
      onSuccess: (data) => {
        if (handleSuccess) handleSuccess(data);
      },
      onError: (error) => {
        console.log({ error });
      },
    }
  );

  return {
    findTask: mutateAsync,
    isLoading,
    isError,
    task: data,
  };
};

interface IGetAutomationExecutionsProps {
  automationId: string;
  campaignIds: string[];
  limit?: number;
}

/**
 * Hook to fetch the last N executions for an automation across multiple campaigns.
 * Fetches tasks for all campaigns that use the automation, filters by automation ID,
 * sorts by created_on descending, and returns the last N executions.
 * 
 * @param automationId - The automation ID to filter tasks by
 * @param campaignIds - Array of campaign IDs that use this automation
 * @param limit - Maximum number of executions to return (default: 10)
 */
export const useGetAutomationExecutions = ({ automationId, campaignIds, limit = 10 }: IGetAutomationExecutionsProps) => {
  const { data = [], isLoading, isFetching } = useQuery(
    ['automation-executions', automationId, campaignIds.join(',')],
    async () => {
      if (!automationId || !campaignIds || campaignIds.length === 0) {
        return [];
      }

      // Fetch tasks for all campaigns and both task types in parallel
      const taskPromises: Promise<ITask[]>[] = [];
      
      campaignIds.forEach((campaignId) => {
        // Fetch ADS_AUTOMATION tasks
        taskPromises.push(
          TasksService.listByCampaign(campaignId, 0, 'ADS_AUTOMATION', {
            sortBy: 'created_on',
            sortOrder: 'desc',
          }).catch(() => [])
        );

        // Fetch CAMPAIGN_BUDGET tasks
        taskPromises.push(
          TasksService.listByCampaign(campaignId, 0, 'CAMPAIGN_BUDGET', {
            sortBy: 'created_on',
            sortOrder: 'desc',
          }).catch(() => [])
        );
      });

      const allTasksArrays = await Promise.all(taskPromises);
      const allTasks = allTasksArrays.flat();

      // Convert automationId to string for comparison (handles ObjectId cases)
      const automationIdStr = String(automationId);
      const automationTasks = allTasks.filter((task: ITask) => {
        const taskAutomationId = task.automation?.automation;
        return String(taskAutomationId) === automationIdStr;
      });

      // Sort by created_on descending (most recent first)
      const sortedTasks = automationTasks.sort((a: ITask, b: ITask) => {
        const dateA = a.created_on ? new Date(a.created_on).getTime() : 0;
        const dateB = b.created_on ? new Date(b.created_on).getTime() : 0;
        return dateB - dateA;
      });

      // Return the last N executions
      return sortedTasks.slice(0, limit);
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60, // 1 minute
      enabled: !!automationId && campaignIds.length > 0,
    }
  );

  return {
    executions: data,
    isLoading,
    isFetching,
  };
};
